import React, { useState, useContext, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  Badge, Box, Drawer, IconButton, ListItem, ListItemButton,
  Typography, debounce, styled
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import MenuIcon from "@mui/icons-material/Menu";
import { useCart } from "react-use-cart";
import { FavoriteBorderOutlined } from "@mui/icons-material";
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';

import logo from "./../../assets/buraq_logo.jpg";
import ajaxService from "../../services/ajax-service";
import { ImageURL } from "../../constants";
import { AuthContext } from "../../AuthContext";
import { useTranslation } from "react-i18next";
import defaultImage from "../../assets/contactsvg.svg";
import MobileNavigationDrawer from "./MobileNavigationDrawer";

const StyledBadge = styled(Badge)(({ theme }) => ({
  "& .MuiBadge-badge": {
    right: -3,
    top: 13,
    border: `2px solid white`,
    padding: "0 4px",
  },
}));

const HeaderBar = () => {
  const { t, i18n } = useTranslation();
  const { logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();

  const isRTL = i18n.language === "ar" || i18n.language === "ur";
  const isLoggedIn = !!localStorage.getItem("token");
  const { totalUniqueItems } = useCart();

  const [loading, setLoading] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [products, setProducts] = useState([]);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  const itemRefs = useRef([]);
  const desktopSearchRef = useRef(null);
  const mobileSearchRef = useRef(null);
  const mobileSearchIconRef = useRef(null);

  const handleNavigate = () => {
    navigate(isLoggedIn ? "/user/wishlists" : "/user/login");
  };

  useEffect(() => {
    setSearch("");
    setProducts([]);
    setSearchOpen(false);
    setHighlightedIndex(-1);
  }, [location.pathname]);

  const handleLogout = async () => {
    ajaxService.get("/auth/logout", 0, true);
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    localStorage.removeItem("expires_at");
    logout();
    navigate("/");
  };

  const handleLogoClick = (e) => {
    if (location.pathname === "/") {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 100);
    }
  };

  const handleProductClick = async (slug) => {
    ajaxService.get("/product/search-log/" + slug);
    setSearch("");
    setProducts([]);
    setHighlightedIndex(-1);
    setSearchOpen(false);
    navigate("/product/" + slug);
  };

  const fetchProducts = async (query) => {
    if (!query) {
      setProducts([]);
      setHighlightedIndex(-1);
      return;
    }
    try {
      setLoading(true);
      const response = await ajaxService.get(`/product-search?q=${query}`);
      setProducts(response);
      setHighlightedIndex(response?.length ? 0 : -1);
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  };

  const debouncedFetchProducts = debounce((query) => fetchProducts(query), 500);

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearch(query);
    debouncedFetchProducts(query);
    if (!query) setHighlightedIndex(-1);
  };

  const handleKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!products.length) return;
      setHighlightedIndex((prev) => Math.min(prev + 1, products.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      if (!products.length) return;
      setHighlightedIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === "Enter") {
      if (highlightedIndex >= 0 && products[highlightedIndex]?.slug)
        handleProductClick(products[highlightedIndex].slug);
    } else if (e.key === "Escape") {
      setSearch("");
      setProducts([]);
      setHighlightedIndex(-1);
      setSearchOpen(false);
    }
  };

  useEffect(() => {
    if (highlightedIndex >= 0 && itemRefs.current[highlightedIndex])
      itemRefs.current[highlightedIndex].scrollIntoView({ block: "nearest" });
  }, [highlightedIndex]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        desktopSearchRef.current &&
        !desktopSearchRef.current.contains(event.target) &&
        search &&
        window.innerWidth >= 640
      ) {
        setSearch("");
        setProducts([]);
        setHighlightedIndex(-1);
      }
      if (
        searchOpen &&
        mobileSearchRef.current &&
        !mobileSearchRef.current.contains(event.target) &&
        !(mobileSearchIconRef.current && mobileSearchIconRef.current.contains(event.target))
      ) {
        setSearchOpen(false);
        setSearch("");
        setProducts([]);
        setHighlightedIndex(-1);
      }
    };

    if (search || searchOpen)
      document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [search, searchOpen]);

  return (
    <Box
      sx={{
        width: "100%",
        position: "relative",
        top: 0,
        bgcolor: "white",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        // Responsive height
        height: 'auto',
        padding:'5px',
        // Responsive horizontal padding — tighter at md to prevent overflow
        px: { xs: 1.5, sm: 2, md: 2, lg: 4, xl: 6 },
        gap: { xs: 1, sm: 2, md: 2, lg: 3 },
        boxSizing: "border-box",
        overflow: "visible",
      }}
    >
      {/* ── LEFT: Hamburger + Logo ── */}
      <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0, gap: { xs: 0.5, sm: 1 } }}>
        <IconButton
          sx={{ p: 0, display: { lg: "none" } }}
          onClick={() => setMobileDrawerOpen(true)}
        >
          <MenuIcon sx={{ fontSize: { xs: 26, sm: 30 }, color: "#2858A3" }} />
        </IconButton>

        <MobileNavigationDrawer
          open={mobileDrawerOpen}
          onClose={() => setMobileDrawerOpen(false)}
        />

        <Link to="/" onClick={handleLogoClick}>
          <Box
            component="img"
            src={logo}
            alt="burraq star logo"
            sx={{
              height: { xs: 30, sm: 38, md: 44, lg: 45 },
              width: "auto",
              objectFit: "contain",
              display: "block",
            }}
          />
        </Link>
      </Box>

      {/* ── CENTRE: Desktop Search Bar ── */}
      <Box
        ref={desktopSearchRef}
        sx={{
          // Flex-grow fills the space between logo and icons
          flex: "1 1 auto",
          minWidth: 0,
          maxWidth: { sm: 480, md: 520, lg: 620, xl: 740 },
          position: "relative",
          display: { xs: "none", sm: "flex" },
          alignItems: "center",
          bgcolor: "#f3f4f6",
          borderRadius: "999px",
          border: "1px solid #e5e7eb",
          px: 2,
          height: { sm: 40, md: 44, lg: 38 },
        }}
      >
        <input
          style={{
            flex: 1,
            background: "transparent",
            outline: "none",
            border: "none",
            fontSize: "0.875rem",
            color: "#2858A3",
            fontFamily: "Poppins, sans-serif",
            minWidth: 0,         // crucial: lets the input shrink
          }}
          placeholder={t("header.search_placeholder")}
          value={search}
          onChange={handleSearchChange}
          onKeyDown={handleKeyDown}
        />

        <Box
          sx={{
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "#2858A3",
            borderRadius: "999px",
            mr: "-9px",
            p: "4px",
            transition: "background 0.2s",
            "&:hover": { bgcolor: "#02AFF3" },
          }}
        >
          <SearchOutlinedIcon style={{ fontSize: 22, color: "white", padding: 2 }} />
        </Box>

        {/* Dropdown */}
        {search && (
          <Box
            sx={{
              position: "absolute",
              top: "calc(100% + 8px)",
              left: 0,
              width: "100%",
              bgcolor: "white",
              borderRadius: "12px",
              boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
              zIndex: 1050,
              maxHeight: "50vh",
              overflowY: "auto",
            }}
          >
            {loading && (
              <Typography sx={{ textAlign: "center", color: "#9ca3af", p: 2, fontSize: "0.875rem" }}>
                {t("header.searching")}
              </Typography>
            )}
            {products.length > 0
              ? products.map((product, i) => (
                  <ListItem
                    key={product.slug || `key-${i}`}
                    disablePadding
                    onClick={() => handleProductClick(product.slug)}
                    onMouseEnter={() => setHighlightedIndex(i)}
                    ref={(el) => { itemRefs.current[i] = el; }}
                  >
                    <ListItemButton sx={{ bgcolor: highlightedIndex === i ? "#e6f0fb" : "transparent" }}>
                      <Box sx={{ display: "flex", alignItems: "center", width: "100%", gap: 1.5 }}>
                        <Box
                          component="img"
                          src={product.images?.length > 0 ? ImageURL + product.images[0] : defaultImage}
                          alt={product.name}
                          onError={(e) => { e.target.onerror = null; e.target.src = "/default-image.jpg"; }}
                          sx={{ height: 40, width: 40, objectFit: "contain", flexShrink: 0 }}
                        />
                        <Typography sx={{ fontSize: "0.75rem", color: "black", fontFamily: "Poppins, sans-serif" }}>
                          {product.name}
                        </Typography>
                      </Box>
                    </ListItemButton>
                  </ListItem>
                ))
              : !loading && (
                  <Typography sx={{ textAlign: "center", color: "#9ca3af", p: 2, fontSize: "0.875rem" }}>
                    {t("header.no_products_found")}
                  </Typography>
                )}
          </Box>
        )}
      </Box>

      {/* ── RIGHT: Icons ── */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexShrink: 0,
          gap: { xs: 0.5, sm: 1, md: 1, lg: 2 },
        }}
      >
        {/* Mobile search icon (hidden sm+) */}
        <Box ref={mobileSearchIconRef} sx={{ display: { sm: "none" } }}>
          <IconButton
            size="small"
            onClick={() => {
              setSearchOpen((prev) => !prev);
              if (!searchOpen) { setSearch(""); setProducts([]); setHighlightedIndex(-1); }
            }}
          >
            <SearchIcon sx={{ fontSize: 22, color: "#2858A3" }} />
          </IconButton>
        </Box>

        {/* Wishlist */}
        <IconButton size="small" onClick={handleNavigate} sx={{ p: { xs: 0.5, lg: 1 } }}>
          <FavoriteBorderOutlined sx={{ fontSize: { xs: 22, sm: 24, lg: 28 } }} />
        </IconButton>

        {/* Cart */}
        <Link to="/checkout">
          <IconButton sx={{ p: { xs: 0.5, sm: 1 } }}>
            <StyledBadge badgeContent={totalUniqueItems} color="primary">
              <ShoppingCartIcon sx={{ color: "#2858A3", fontSize: { xs: 22, sm: 24, lg: 30 } }} />
            </StyledBadge>
          </IconButton>
        </Link>
      </Box>

      {/* ── Mobile Search Overlay ── */}
      {searchOpen && (
        <Box
          ref={mobileSearchRef}
          sx={{
            position: "absolute",
            bottom: -60,
            left: 0,
            width: "100%",
            zIndex: 50,
            bgcolor: "white",
            borderTop: "1px solid #e5e7eb",
            boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
            p: 1.5,
            display: { sm: "none" },
          }}
        >
          <Box
            sx={{
              width: "100%",
              bgcolor: "#f0f0f0",
              borderRadius: "8px",
              display: "flex",
              alignItems: "center",
              px: 2,
              py: 1.5,
              minHeight: 48,
            }}
          >
            <input
              style={{
                width: "100%",
                background: "transparent",
                outline: "none",
                border: "none",
                color: "#2858A3",
                fontSize: "1rem",
                fontFamily: "Poppins, sans-serif",
              }}
              placeholder={t("header.search_placeholder")}
              value={search}
              onChange={handleSearchChange}
              onKeyDown={handleKeyDown}
              autoFocus
            />
          </Box>

          {search.length > 0 && (
            <Box
              sx={{
                position: "absolute",
                top: 68,
                left: 12,
                right: 12,
                borderRadius: "8px",
                zIndex: 50,
                maxHeight: "40vh",
                overflowY: "auto",
                bgcolor: "white",
                border: "1px solid #e5e7eb",
                boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
              }}
            >
              {products
                .filter((prod) => prod.name.toLowerCase().includes(search.toLowerCase()))
                .map((product, i) => {
                  const productIndex = products.findIndex((p) => p.slug === product.slug);
                  return (
                    <ListItem
                      key={product.slug || `mobile-${i}`}
                      disablePadding
                      onClick={() => handleProductClick(product.slug)}
                      onMouseEnter={() => setHighlightedIndex(productIndex)}
                      ref={(el) => { if (productIndex > -1) itemRefs.current[productIndex] = el; }}
                    >
                      <ListItemButton
                        sx={{
                          borderRadius: "4px",
                          p: 1,
                          bgcolor: highlightedIndex === productIndex ? "#e6f0fb" : "transparent",
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, width: "100%" }}>
                          <Box
                            component="img"
                            src={product?.images?.[0] ? ImageURL + product.images[0] : defaultImage}
                            alt={product.name}
                            sx={{ height: 32, width: 32, borderRadius: "4px", objectFit: "contain", flexShrink: 0 }}
                          />
                          <Typography sx={{ fontSize: "0.75rem", color: "black", fontFamily: "Poppins, sans-serif", flex: 1 }}>
                            {product.name}
                          </Typography>
                        </Box>
                      </ListItemButton>
                    </ListItem>
                  );
                })}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default HeaderBar;